import { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ResourceCard } from '../components/ResourceCard';
import { Search, Filter, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resourceTypes = [
  { value: 'all', label: 'Tous les types' },
  { value: 'comic', label: 'Bandes dessinées' },
  { value: 'worksheet', label: 'Fiches' },
  { value: 'video', label: 'Vidéos' },
  { value: 'podcast', label: 'Podcasts' },
  { value: 'tool', label: 'Outils' }
];

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resourceType, setResourceType] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 12;

  useEffect(() => {
    fetchResources();
  }, [page, resourceType]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit.toString()
      });
      
      if (search) params.append('search', search);
      if (resourceType && resourceType !== 'all') params.append('resource_type', resourceType);

      const res = await fetch(`${API_URL}/api/resources?${params}`);
      if (res.ok) {
        const data = await res.json();
        setResources(data.resources || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch resources:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchResources();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div data-testid="resources-page" className="min-h-screen py-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <span className="text-xs uppercase tracking-widest text-secondary font-medium">
            Outils thérapeutiques
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2 mb-4">
            Ressources
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Découvrez nos outils d'accompagnement thérapeutique : bandes dessinées, fiches pratiques, 
            vidéos explicatives et podcasts.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher une ressource..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">
              Rechercher
            </Button>
          </form>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={resourceType} onValueChange={(value) => { setResourceType(value); setPage(0); }}>
              <SelectTrigger className="w-48" data-testid="type-filter">
                <SelectValue placeholder="Type de ressource" />
              </SelectTrigger>
              <SelectContent>
                {resourceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-muted-foreground mb-6">
          {total} ressource{total !== 1 ? 's' : ''} trouvée{total !== 1 ? 's' : ''}
        </p>

        {/* Resources Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : resources.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map((resource) => (
                <ResourceCard key={resource.resource_id} resource={resource} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Précédent
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {page + 1} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Aucune ressource trouvée</p>
            <p className="text-sm text-muted-foreground mt-2">
              Les ressources seront bientôt disponibles.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
